import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { configureStore } from "@reduxjs/toolkit";
import globalReducer from "state";
import { Provider } from "react-redux";
import { setupListeners } from "@reduxjs/toolkit/query";
import { api } from "state/api";
import { ContextProvider } from "contexts/ContextProvider";
import { AuthProvider } from "contexts/AuthContext";


const isResizeObserverNoise = (value) => {
  const text = String(value || "");
  return (
    text.includes("ResizeObserver loop completed with undelivered notifications") ||
    text.includes("ResizeObserver loop limit exceeded")
  );
};

// Global unhandled promise rejection handler
// Prevents app freeze when axios requests fail without .catch()
window.addEventListener('unhandledrejection', (event) => {
  const reasonMessage =
    event?.reason?.message ||
    event?.reason ||
    "";

  if (isResizeObserverNoise(reasonMessage)) {
    event.preventDefault();
    return;
  }

  const errorUrl = event.reason?.config?.url || event.reason?.config?.baseURL || 'unknown';
  console.error('Unhandled promise rejection:', event.reason, 'URL:', errorUrl);
  
  // Don't prevent default for network errors - just log them
  if (event.reason?.code === 'ERR_NETWORK' || event.reason?.name === 'AxiosError') {
    console.warn('Network error caught by global handler:', event.reason.message, 'URL:', errorUrl);
    // Prevent the error from propagating and freezing the app
    event.preventDefault();
  }
});

// Suppress known benign browser ResizeObserver noise that can appear when
// MUI components reflow (especially multiline text fields) in development.
// Keep all other runtime errors visible.
window.addEventListener('error', (event) => {
  const message = String(event?.message || '');
  const stack = String(event?.error?.stack || '');

  const isResizeObserverEvent = isResizeObserverNoise(message) || isResizeObserverNoise(stack);

  if (isResizeObserverEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
}, true);

// Some development overlays forward this as console.error instead of window error.
// Filter only this known benign ResizeObserver warning.
const originalConsoleError = console.error;
console.error = (...args) => {
  if (args.some((arg) => isResizeObserverNoise(arg?.message || arg))) {
    return;
  }
  originalConsoleError(...args);
};

const store = configureStore({
  reducer: {
    global: globalReducer,
    [api.reducerPath]: api.reducer,
  },
  middleware: (getDefault) => getDefault().concat(api.middleware),
});

setupListeners(store.dispatch);

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <ContextProvider>
    <AuthProvider>
      <React.StrictMode>
        <Provider store={store}>
          <App />
        </Provider>
      </React.StrictMode>
    </AuthProvider>
  </ContextProvider>
);
